export const currentGoldPrice = 785.12;

export interface DepositOption {
  label: string;
  value: number;
  lockPeriod: string;
  isRecommended?: boolean;
}

// 仓管费配置：元/克
export const storageFeeConfig: Record<string, { rate: number; display: string }> = {
  gold: { rate: 0.025, display: '每天' },
  silver: { rate: 0.0016667, display: '每天' },
  kiloBar: { rate: 0.015, display: '每天' },
};

/** 计算仓管费 */
export function calcStorageFee(productType: 'gold' | 'silver', isKiloBar: boolean, weight: number): number {
  if (isKiloBar) {
    return Number((weight * storageFeeConfig.kiloBar.rate).toFixed(2));
  }
  const rate = productType === 'silver' ? storageFeeConfig.silver.rate : storageFeeConfig.gold.rate;
  return Number((weight * rate).toFixed(2));
}

// 工费配置：元/克（下单一次性收取）
export const craftFeeConfig: Record<string, { rate: number; display: string }> = {
  gold: { rate: 1, display: '1元/克' },
  silver: { rate: 0.1, display: '0.1元/克' },
  kiloBar: { rate: 1.25, display: '1.25元/克' },
};

/** 计算工费 */
export function calcCraftFee(productType: 'gold' | 'silver', isKiloBar: boolean, weight: number): number {
  if (isKiloBar) {
    return Number((weight * craftFeeConfig.kiloBar.rate).toFixed(2));
  }
  const rate = productType === 'silver' ? craftFeeConfig.silver.rate : craftFeeConfig.gold.rate;
  return Number((weight * rate).toFixed(2));
}

/** 解析锁价周期为天数 */
export function parseLockDays(lockPeriod: string): number {
  const match = lockPeriod.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 30;
}

export const goldDepositOptions: DepositOption[] = [
  { label: '3%', value: 0.03, lockPeriod: '7天', isRecommended: false },
  { label: '6%', value: 0.06, lockPeriod: '30天', isRecommended: true },
  { label: '10%', value: 0.10, lockPeriod: '60天', isRecommended: false },
  { label: '20%', value: 0.20, lockPeriod: '90天', isRecommended: false },
];

export const silverDepositOptions: DepositOption[] = [
  { label: '3%', value: 0.03, lockPeriod: '7天', isRecommended: false },
  { label: '6%', value: 0.06, lockPeriod: '30天', isRecommended: true },
  { label: '10%', value: 0.10, lockPeriod: '60天', isRecommended: false },
  { label: '20%', value: 0.20, lockPeriod: '90天', isRecommended: false },
];

export const lockTerms = {
  note: '预约锁价成功后需在有效期内完成全额付款，请在预约有效期内前往指定网点进行实物交付或选择邮寄配送。',
  agreementName: '实物黄金预约锁价协议',
  riskName: '风险提示书',
};

export const expirationRules = [
  '1. 补足货款申请履约',
  '2. 申请解除预约或部分履约',
  '3. 补充预约资金延长锁价期限',
];

export interface TradeProduct {
  id: string;
  name: string;
  code: string;
  price: number;
  unit: string;
  type: 'gold' | 'silver';
  isKiloBar?: boolean;
}

export const tradeProducts: TradeProduct[] = [
  { id: 'au9999', name: '黄金', code: 'AU9999', price: 800.0, unit: '克', type: 'gold' },
  { id: 'ag9999', name: '白银', code: 'Ag9999', price: 6.82, unit: '克', type: 'silver' },
  { id: 'kgbar', name: '公斤条', code: '1KG', price: 800.0, unit: '克', type: 'gold', isKiloBar: true },
];

export const lockPriceProducts = [
  {
    id: 1,
    name: 'AU 999.9 投资金条',
    spec: '10g',
    price: 7851.20,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=Gold%20bar%20bullion%20on%20dark%20surface%2C%20premium%20quality%2C%20shiny%20metallic%20texture%2C%20professional%20product%20photography%2C%20warm%20golden%20lighting%2C%20minimalist%20composition&width=300&height=300&seq=gold-bar-10g&orientation=squarish',
    stock: 500,
  },
  {
    id: 2,
    name: 'AU 999.9 投资金条',
    spec: '20g',
    price: 15702.40,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=Gold%20bar%20bullion%20on%20black%20velvet%20surface%2C%20high%20purity%2C%20reflective%20surface%2C%20luxury%20product%20photography%2C%20warm%20ambient%20lighting&width=300&height=300&seq=gold-bar-20g&orientation=squarish',
    stock: 300,
  },
  {
    id: 3,
    name: 'AU 999.9 投资金条',
    spec: '50g',
    price: 39256.00,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=Stacked%20gold%20bars%20on%20dark%20surface%2C%20investment%20grade%2C%20shiny%20metallic%20luster%2C%20professional%20product%20photography%2C%20dramatic%20lighting&width=300&height=300&seq=gold-bar-50g&orientation=squarish',
    stock: 200,
  },
  {
    id: 4,
    name: 'AU 999.9 投资金条',
    spec: '100g',
    price: 78512.00,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=Large%20gold%20bullion%20bar%20on%20black%20surface%2C%20high%20purity%20gold%2C%20reflective%20metallic%20surface%2C%20luxury%20investment%20photography%2C%20warm%20golden%20tones&width=300&height=300&seq=gold-bar-100g&orientation=squarish',
    stock: 100,
  },
  {
    id: 5,
    name: 'AU 999.9 投资金条',
    spec: '500g',
    price: 392560.00,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=Premium%20gold%20bullion%20bar%20on%20dark%20surface%2C%20heavy%20gold%20bar%2C%20shiny%20metallic%20reflection%2C%20professional%20luxury%20photography%2C%20warm%20lighting&width=300&height=300&seq=gold-bar-500g&orientation=squarish',
    stock: 50,
  },
  {
    id: 6,
    name: 'AU 999.9 投资金条',
    spec: '1kg',
    price: 785120.00,
    unitPrice: 785.12,
    image: 'https://readdy.ai/api/search-image?query=One%20kilogram%20gold%20bullion%20bar%20on%20black%20surface%2C%20large%20gold%20investment%2C%20shiny%20metallic%20luster%2C%20premium%20product%20photography%2C%20dramatic%20warm%20lighting&width=300&height=300&seq=gold-bar-1kg&orientation=squarish',
    stock: 20,
  },
];

export const riskDisclaimerContent = [
  {
    title: '投资有风险，入市需谨慎',
    body: '贵金属价格受国际市场供求关系、地缘政治、货币政策等多重因素影响，价格波动剧烈。投资者应当充分了解市场风险，根据自身风险承受能力谨慎决策，切勿盲目跟风操作。',
  },
  {
    title: '价格波动风险',
    body: '黄金、白银等贵金属价格受全球经济形势、美元汇率、通胀预期、地缘政治事件等因素影响，可能出现大幅波动。投资者可能面临本金亏损的风险，过往价格表现不代表未来收益。',
  },
  {
    title: '杠杆交易风险',
    body: '如使用杠杆或保证金方式进行交易，价格小幅波动可能导致较大盈亏。当市场走势与预期相反时，可能面临追加保证金或被强制平仓的风险，损失可能超过初始投入。',
  },
  {
    title: '流动性风险',
    body: '在市场极端情况下，可能出现买卖价差扩大、订单无法及时成交或成交价格偏离预期的情况。投资者应充分评估自身的流动性需求。',
  },
  {
    title: '政策及合规风险',
    body: '国家有关贵金属交易的政策法规可能发生变化，相关监管要求也可能调整，可能对交易规则、费用标准等产生影响。投资者应及时关注政策动态。',
  },
  {
    title: '系统与技术风险',
    body: '网络延迟、系统故障、数据传输异常等技术问题可能导致交易指令延迟执行或无法执行。建议投资者保留相关交易记录作为凭证。',
  },
  {
    title: '不可抗力风险',
    body: '因自然灾害、战争、恐怖袭击、政府行为等不可抗力因素导致交易中断、数据丢失或资产损失的，平台不承担赔偿责任。',
  },
  {
    title: '特别提示',
    body: '本提示书旨在向投资者揭示贵金属投资的主要风险，但并未涵盖所有风险。投资者在参与交易前，应仔细阅读相关协议条款，充分理解交易规则，必要时可咨询专业投资顾问。',
  },
];